/*
PURPOSE: URL detection and sanitization utility for auto-linking in comments

USE CASES:
- Detect URLs in user-generated text content
- Sanitize URLs for security (prevent XSS attacks)
- Extract URL information for rendering as clickable links
- Support various URL formats (http, https, www, naked domains)

DOMAIN BOUNDARIES:
- Responsible for URL detection and validation only
- Does NOT handle rendering (handled by AutoLink component)
- Does NOT store or persist URLs
- Security-focused: blocks malicious URL schemes

ASSUMPTIONS:
- Input text may contain mixed content (URLs and regular text)
- URLs should be made clickable but open in new tabs for security
- Malicious schemes (javascript:, data:, etc.) should be blocked
*/

// Comprehensive URL regex that matches various formats
const URL_REGEX = /(?:(?:https?:\/\/)|(?:www\.)|(?:[\w-]+\.(?:com|org|net|edu|gov|mil|int|co|uk|de|fr|jp|cn|au|ca|in|br|mx|es|it|nl|se|no|dk|fi|pl|ru|ch|at|be|cz|hu|ie|pt|gr|bg|ro|hr|si|sk|lt|lv|ee|lu|mt|cy|is|li|mc|sm|va|ad|md|by|ua|rs|ba|mk|al|me|xk|ly|eg|ma|tn|dz|ng|za|ke|gh|ug|tz|zm|zw|bw|mw|sz|ls|na|ao|mz|mg|mu|sc|cv|gm|gn|gw|sl|lr|ci|bf|ml|ne|td|cf|cm|ga|cg|cd|st|gq|ao|so|dj|er|et|sd|ss|ye|om|ae|qa|bh|kw|sa|iq|ir|af|pk|bd|lk|mv|np|bt|mm|th|la|kh|vn|my|sg|bn|ph|id|tl|pg|sb|vu|nc|fj|ki|nr|pw|fm|mh|ws|to|tv|nu|ck|pf|as|gu|mp|vi|pr|aw|an|bb|bs|bz|cr|cu|dm|do|gd|gt|ht|hn|jm|kn|lc|mx|ni|pa|sv|tt|vc|vg|ai|ag|tc)))[\w\-._~:/?#[\]@!$&'()*+,;=%]+/gi;

// Malicious URL schemes to block
const BLOCKED_SCHEMES = ['javascript:', 'data:', 'vbscript:', 'file:', 'ftp:'];

export interface URLInfo {
  url: string;
  originalText: string;
  startIndex: number;
  endIndex: number;
  isValid: boolean;
}

/**
 * Detects URLs in text and returns information about each URL found
 */
export function detectURLs(text: string): URLInfo[] {
  const urls: URLInfo[] = [];
  const matches = text.matchAll(URL_REGEX);

  for (const match of matches) {
    if (!match.index) continue;

    const originalText = match[0];
    const startIndex = match.index;
    const endIndex = startIndex + originalText.length;
    
    // Sanitize and validate the URL
    const sanitizedUrl = sanitizeURL(originalText);
    const isValid = isValidURL(sanitizedUrl);

    urls.push({
      url: sanitizedUrl,
      originalText,
      startIndex,
      endIndex,
      isValid
    });
  }

  return urls;
}

/**
 * Sanitizes a URL to ensure it's safe for linking
 */
export function sanitizeURL(url: string): string {
  // Trim whitespace
  const trimmed = url.trim();
  
  // Check for blocked schemes
  const lowerUrl = trimmed.toLowerCase();
  for (const scheme of BLOCKED_SCHEMES) {
    if (lowerUrl.startsWith(scheme)) {
      return '#'; // Return invalid URL for blocked schemes
    }
  }

  // Add protocol if missing
  if (!trimmed.match(/^https?:\/\//i)) {
    // If starts with www., add https://
    if (trimmed.match(/^www\./i)) {
      return `https://${trimmed}`;
    }
    // If it looks like a domain, add https://
    if (trimmed.match(/^[\w-]+\.[\w.]+/)) {
      return `https://${trimmed}`;
    }
  }

  return trimmed;
}

/**
 * Validates if a URL is safe and properly formatted
 */
export function isValidURL(url: string): boolean {
  try {
    const urlObject = new URL(url);
    
    // Check protocol
    if (!['http:', 'https:'].includes(urlObject.protocol)) {
      return false;
    }

    // Check for blocked schemes in the URL
    const lowerUrl = url.toLowerCase();
    for (const scheme of BLOCKED_SCHEMES) {
      if (lowerUrl.includes(scheme)) {
        return false;
      }
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * Generic interface for detected items in text (URLs, mentions, etc.)
 */
export interface DetectedItem {
  startIndex: number;
  endIndex: number;
}

/**
 * Processes text and returns segments with URL information
 * Useful for rendering mixed text/URL content
 */
export interface TextSegment {
  text: string;
  isURL: boolean;
  urlInfo?: URLInfo;
}

/**
 * Generic function to segment text around detected items
 * @param text - The original text to segment
 * @param items - Array of detected items with start/end indices
 * @param createSegment - Function to create a segment from an item
 * @returns Array of text segments
 */
export function segmentText<T extends DetectedItem, S>(
  text: string,
  items: T[],
  createSegment: (item: T, text: string) => S,
  createTextSegment: (text: string) => S
): S[] {
  const segments: S[] = [];

  if (items.length === 0) {
    segments.push(createTextSegment(text));
    return segments;
  }

  let lastIndex = 0;

  for (const item of items) {
    // Add text before this item
    if (item.startIndex > lastIndex) {
      const textBefore = text.slice(lastIndex, item.startIndex);
      segments.push(createTextSegment(textBefore));
    }

    // Add the item segment
    const itemText = text.slice(item.startIndex, item.endIndex);
    segments.push(createSegment(item, itemText));

    lastIndex = item.endIndex;
  }

  // Add remaining text after the last item
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    segments.push(createTextSegment(remainingText));
  }

  return segments;
}

export function processTextWithURLs(text: string): TextSegment[] {
  const urls = detectURLs(text);

  return segmentText<URLInfo, TextSegment>(
    text,
    urls,
    (urlInfo, itemText) => ({
      text: itemText,
      isURL: true,
      urlInfo
    }),
    (plainText) => ({
      text: plainText,
      isURL: false
    })
  );
}